/**
 * [2026-09-12 사용자합의] 폼 검증 실패 시 "첫 번째 에러 필드로 스크롤 + 포커스".
 *
 * WHY: 검증 실패 시 UX 표준을 정할 때 두 안을 비교 —
 *   (A) 페이지 최상단으로 고정 이동: 경각심은 주지만 에러가 아래 있으면 사용자가 다시 찾아야 함.
 *   (B) 첫 에러 필드로 스크롤+포커스(채택): 사용자가 즉시 문제 지점을 보고 바로 입력. 필드는 이미
 *       빨간 테두리(.err)+인라인 힌트가 있으므로 별도 요약 배너 불필요.
 *   에러가 1개든 여러 개든 "시각 순서상 첫 에러"로 항상 이동 → 경각심 + 완결성 둘 다 충족.
 *
 * 사용법:
 *   - 각 입력(input/select/textarea)에 name={feKey} 부여(예: name="username").
 *   - input이 없는 블록(약관 체크박스 묶음 등)엔 data-field={feKey} 부여(예: data-field="terms").
 *   - 검증 후 setFe(fx); scrollToFirstError(formRef.current, ORDER, fx); 순으로 호출.
 *   - ORDER는 화면상 위→아래 순서의 feKey 배열(첫 에러 판정 기준).
 *
 * ★스크롤 방식(중요): 이 사이트는 Lenis 관성 스크롤(App의 useLenis 싱글턴, html.lenis)이 전역으로 걸려 있어
 *   네이티브 el.scrollIntoView({behavior:'smooth'})가 Lenis에 의해 되돌려져(예: 52px만 이동 후 멈춤) 목표에
 *   도달하지 못함. → Products.tsx와 동일하게 getLenis().scrollTo(el, {offset,immediate})로 직접 스크롤한다.
 *   offset -150 = 고정 헤더(약 150px) 아래로 필드가 오도록. Lenis 없으면 네이티브로 폴백.
 *   focus는 preventScroll:true로 주어 포커스가 스크롤을 가로채지 않게 함(스크롤은 Lenis가 담당).
 * 연관: Signup.tsx / SocialComplete.tsx 의 submit() 검증부, hooks/useLenis.ts(getLenis), Products.tsx:136~139.
 */
import { getLenis } from '@/hooks/useLenis';

export function scrollToFirstError(
	container: HTMLElement | null,
	order: string[],
	fe: Record<string, string>,
): void {
	if (!container) return;
	const firstKey = order.find((k) => fe[k]);
	if (!firstKey) return;
	// ★동기 실행(★rAF 쓰지 말 것): 대상은 name/data-field로 찾으므로 .err 클래스 반영을 기다릴 필요가 없다.
	//   rAF로 감쌌더니 "백그라운드(hidden) 탭에서 rAF가 정지"해 포커스조차 안 되는 문제가 있었음(2026-09-12 실측).
	//   동기로 두면 포커스는 항상 보장되고, 스크롤은 Lenis가 담당(포그라운드에서 관성 애니메이션).
	const el = (container.querySelector(`[name="${firstKey}"]`)
		|| container.querySelector(`[data-field="${firstKey}"]`)) as HTMLElement | null;
	if (!el) return;
	// 포커스 먼저(스크롤과 독립적으로 항상 보장). preventScroll로 포커스가 스크롤을 가로채지 않게.
	const focusable = (el.matches('input,select,textarea')
		? el
		: el.querySelector('input,select,textarea,button')) as HTMLElement | null;
	focusable?.focus({ preventScroll: true });
	const lenis = getLenis();
	if (lenis) lenis.scrollTo(el, { offset: -150 });   // 관성 애니메이션(부드럽게). 헤더 아래로.
	else el.scrollIntoView({ behavior: 'smooth', block: 'center' });   // 폴백(Lenis 미가동 환경)
}
